import { Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getCookie } from "../../reducer/utils";
import { Chart } from "react-google-charts";
import moment from "moment";

function LiveOTTAnalytics() {
  const baseURL = process.env.REACT_APP_BASE_URL;
  const toastId = useRef(null);

  const [dateRangeOption, setDateRangeOption] = useState("last_run");
  const [customDateRange, setCustomDateRange] = useState(() => {
    // Live OTT events are marked failed instantly and have precise event times
    const now = moment.utc();
    return {
      start: now.clone().subtract(1, "day").format("YYYY-MM-DDTHH:mm:ss"),
      end: now.format("YYYY-MM-DDTHH:mm:ss"),
    };
  });

  const [analyticsData, setAnalyticsData] = useState({
    passed: [],
    notSubscribed: [],
    failed: [],
    failedWithNotes: [],
  });

  const [loading, setLoading] = useState(false);

  const calculateDateRange = (option) => {
    // Live OTT events are marked failed instantly and have precise event times (not fixed to 00:00 UTC)
    const now = moment.utc();

    switch (option) {
      case "last_run": {
        // Last day of events (past 24 hours)
        const start = now.clone().subtract(1, "day").format("YYYY-MM-DDTHH:mm:ss");
        const end = now.format("YYYY-MM-DDTHH:mm:ss");
        return { start, end, displayStart: start, displayEnd: end };
      }
      case "last_week": {
        const start = now.clone().subtract(1, "week").format("YYYY-MM-DDTHH:mm:ss");
        const end = now.format("YYYY-MM-DDTHH:mm:ss");
        return { start, end, displayStart: start, displayEnd: end };
      }
      case "last_month": {
        const start = now.clone().subtract(1, "month").format("YYYY-MM-DDTHH:mm:ss");
        const end = now.format("YYYY-MM-DDTHH:mm:ss");
        return { start, end, displayStart: start, displayEnd: end };
      }
      case "custom": {
        const start = moment.utc(customDateRange.start).format("YYYY-MM-DDTHH:mm:ss");
        const end = moment.utc(customDateRange.end).format("YYYY-MM-DDTHH:mm:ss");
        return { start, end, displayStart: start, displayEnd: end };
      }
      default: {
        const start = now.clone().subtract(1, "day").format("YYYY-MM-DDTHH:mm:ss");
        const end = now.format("YYYY-MM-DDTHH:mm:ss");
        return { start, end, displayStart: start, displayEnd: end };
      }
    }
  };

  const getAnalytics = async (option = null) => {
    setLoading(true);
    toastId.current = toast.loading("Fetching analytics...");

    try {
      const currentOption = option || dateRangeOption;
      const dateRange = calculateDateRange(currentOption);

      const params = new URLSearchParams({
        start: dateRange.start,
        end: dateRange.end,
        slack_trigger: "false",
      });

      const response = await fetch(
        `${baseURL}/internal/api/v5/utility/live-ott/alert?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch analytics");
      }

      const data = await response.json();

      setAnalyticsData({
        passed: data[0] || [],
        notSubscribed: data[1] || [],
        failed: data[2] || [],
        failedWithNotes: data[3] || [],
      });

      toast.update(toastId.current, {
        render: "Analytics fetched successfully",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
    } catch (error) {
      toast.update(toastId.current, {
        render: `Error: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAnalytics();
  }, []);

  const handleDateRangeOptionChange = (option) => {
    setDateRangeOption(option);

    const dateRange = calculateDateRange(option);
    setCustomDateRange({
      start: dateRange.displayStart,
      end: dateRange.displayEnd,
    });

    if (option !== "custom") {
      getAnalytics(option);
    }
  };

  const handleCustomDateChange = (field, value) => {
    const utcValue = moment.utc(value).format("YYYY-MM-DDTHH:mm:ss");
    setCustomDateRange((prev) => ({
      ...prev,
      [field]: utcValue,
    }));
  };

  const handleApplyCustomRange = () => {
    getAnalytics();
  };

  const failedWithoutNotes = analyticsData.failed.filter(
    (id) => !analyticsData.failedWithNotes.includes(id)
  );

  const chartData = [
    ["Status", "Count"],
    ["Passed", analyticsData.passed.length],
    ["Not Subscribed", analyticsData.notSubscribed.length],
    ["Failed (with notes)", analyticsData.failedWithNotes.length],
    ["Failed (needs attention)", failedWithoutNotes.length],
  ];

  const chartOptions = {
    title: "Live OTT Status",
    pieHole: 0.4,
    is3D: false,
    colors: ["#10B981", "#34D399", "#F59E0B", "#EF4444"],
    legend: {
      position: "bottom",
      alignment: "center",
    },
    chartArea: { width: "90%", height: "70%" },
    pieSliceText: "value",
  };

  const totalCount = analyticsData.passed.length + analyticsData.notSubscribed.length + analyticsData.failed.length;
  const passedPercentage = totalCount > 0
    ? ((analyticsData.passed.length / totalCount) * 100).toFixed(1)
    : 0;
  const notSubscribedPercentage = totalCount > 0
    ? ((analyticsData.notSubscribed.length / totalCount) * 100).toFixed(1)
    : 0;
  const failedWithNotesPercentage = totalCount > 0
    ? ((analyticsData.failedWithNotes.length / totalCount) * 100).toFixed(1)
    : 0;
  const failedWithoutNotesPercentage = totalCount > 0
    ? ((failedWithoutNotes.length / totalCount) * 100).toFixed(1)
    : 0;

  return (
    <>
      <ToastContainer />

      <div className="flex items-center justify-between rounded-lg border-2 border-solid border-grey1 bg-white px-4 py-4">
        <Breadcrumb aria-label="Default breadcrumb example">
          <BreadcrumbItem href="/dashboard" icon={HiHome} />
          <BreadcrumbItem href="/live-ott/event">Live OTT</BreadcrumbItem>
          <BreadcrumbItem className="text-primary1">Analytics</BreadcrumbItem>
        </Breadcrumb>
      </div>

      <div className="mt-4 rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6">
          <h2 className="mb-4 text-lg font-semibold">Live OTT Analytics</h2>

          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="mb-3 text-sm font-medium text-gray-700">
              Event End Time Range Filter (UTC)
            </h3>
            <p className="mb-3 text-xs text-gray-500">
              Note: Live OTT events are marked failed instantly based on their event end time (not fixed to 00:00 UTC)
              <br />
              Retrieves all events with event_end_time between these date-times (inclusive).
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Time Period
                </label>
                <select
                  value={dateRangeOption}
                  onChange={(e) => handleDateRangeOptionChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary1 focus:outline-none focus:ring-1 focus:ring-primary1"
                  disabled={loading}
                >
                  <option value="last_run">Last Day (24 hours)</option>
                  <option value="last_week">Last Week</option>
                  <option value="last_month">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Start Date-Time (UTC)
                </label>
                <input
                  type="datetime-local"
                  step="1"
                  value={customDateRange.start}
                  onChange={(e) => handleCustomDateChange("start", e.target.value)}
                  readOnly={dateRangeOption !== "custom"}
                  className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary1 focus:outline-none focus:ring-1 focus:ring-primary1 ${
                    dateRangeOption !== "custom" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  End Date-Time (UTC)
                </label>
                <input
                  type="datetime-local"
                  step="1"
                  value={customDateRange.end}
                  onChange={(e) => handleCustomDateChange("end", e.target.value)}
                  readOnly={dateRangeOption !== "custom"}
                  className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary1 focus:outline-none focus:ring-1 focus:ring-primary1 ${
                    dateRangeOption !== "custom" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {dateRangeOption === "custom" && (
                <button
                  onClick={handleApplyCustomRange}
                  disabled={loading}
                  className="rounded-md bg-primary1 px-6 py-2 text-sm font-medium text-white hover:bg-primary1/90 disabled:opacity-50"
                >
                  Apply
                </button>
              )}
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Total</div>
              <div className="mt-2 text-3xl font-bold text-gray-900">{totalCount}</div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-green-600">Passed</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-green-700">
                  {analyticsData.passed.length}
                </div>
                <div className="text-sm font-semibold text-green-600">
                  {passedPercentage}%
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-green-600">Not Subscribed</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-green-700">
                  {analyticsData.notSubscribed.length}
                </div>
                <div className="text-sm font-semibold text-green-600">
                  {notSubscribedPercentage}%
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-amber-600">Failed (with notes)</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-amber-700">
                  {analyticsData.failedWithNotes.length}
                </div>
                <div className="text-sm font-semibold text-amber-600">
                  {failedWithNotesPercentage}%
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-red-600">Failed (needs attention)</div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-red-700">
                  {failedWithoutNotes.length}
                </div>
                <div className="text-sm font-semibold text-red-600">
                  {failedWithoutNotesPercentage}%
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
            {totalCount > 0 ? (
              <Chart
                chartType="PieChart"
                data={chartData}
                options={chartOptions}
                width="100%"
                height="400px"
              />
            ) : (
              <div className="flex h-[400px] items-center justify-center text-gray-500">
                No data available for the selected date range
              </div>
            )}
          </div>

          {totalCount > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-green-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-green-700">
                  Passed IDs ({analyticsData.passed.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-green-50 p-3">
                  {analyticsData.passed.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analyticsData.passed.map((id) => (
                        <span
                          key={id}
                          className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No passed IDs</div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-green-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-green-700">
                  Not Subscribed IDs ({analyticsData.notSubscribed.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-green-50 p-3">
                  {analyticsData.notSubscribed.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analyticsData.notSubscribed.map((id) => (
                        <span
                          key={id}
                          className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No not subscribed IDs</div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-amber-700">
                  Failed IDs (with notes) ({analyticsData.failedWithNotes.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-amber-50 p-3">
                  {analyticsData.failedWithNotes.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analyticsData.failedWithNotes.map((id) => (
                        <span
                          key={id}
                          className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No failed IDs with notes</div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-red-200 bg-white p-4">
                <h3 className="mb-3 font-semibold text-red-700">
                  Failed IDs (needs attention) ({failedWithoutNotes.length})
                </h3>
                <div className="max-h-60 overflow-y-auto rounded bg-red-50 p-3">
                  {failedWithoutNotes.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {failedWithoutNotes.map((id) => (
                        <span
                          key={id}
                          className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No failed IDs without notes</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default LiveOTTAnalytics;


